import { PureComponent, ReactNode } from 'react';
import Hotkeys from 'react-hot-keys';
import RightPopUp from '../../../../../common/RightPopUp';
import { CALCULATORS_NAMES } from '../../calculatorsTypes';
import { CALCULATORS } from '../../../../../../constants';
import { CalculatorConfigType, Element } from './types';
import CalculatorInterPretationTable from '../../CalculatorInterpretationTable';
import { AntTooltip } from '../../../../../common/AntTooltip';
import { CalculatorEntity } from '../../../../../../store/types/prescription';
import { ValueObject } from '../../Calculators';
import SliderInput from './SliderInput';
import { EkaFocosableElement } from '../../../../../common/types/EkaFocusable';
import { Input, Menu, Popover, notification } from 'antd';
import SelectInput from './SelectInput';
import { OptionTypeBaseObj } from '../AntSelect';
import DateTimeInput from './DateTimeInput';
import { convertToWeeksAndDays } from './utils';

const SLIDER = 'slider';
const SELECT = 'select';
const DATETIME = 'dateTime';
const validElementTypes = new Set([SLIDER, SELECT, DATETIME]);

interface Result {
    label: string;
    value: string | number;
    ignoreInResults?: boolean;
}

interface Props {
    closeMe: () => void;
    calculatorName: CALCULATORS_NAMES;
    onElementUpdate: (
        obj: CalculatorEntity,
        index?: number,
        itemIdx?: number,
        valueObject?: ValueObject,
    ) => void;
}

interface State {
    calculatorConfig?: CalculatorConfigType;
    tableActiveSection: 'interpretation' | 'how-to-use';
    elements: Element[];
    customJs?: string;
    results?: Result[];
}

class NewCalculator extends PureComponent<Props, State> {
    REF_MAP: (EkaFocosableElement | Input | null)[][] = [];
    state: State = {
        tableActiveSection: 'interpretation',
        elements: [],
    };

    componentDidMount = () => {
        const { calculatorName } = this.props;
        const { fetchCalculatorData } = this;
        if (calculatorName) fetchCalculatorData(calculatorName);
    };

    componentDidUpdate = (prevProps: Readonly<Props>) => {
        const { calculatorName: prevCalculatorName } = prevProps;
        const { calculatorName: newCalculatorName } = this.props;
        const { fetchCalculatorData } = this;
        if (prevCalculatorName !== newCalculatorName) fetchCalculatorData(newCalculatorName);
    };

    fetchCalculatorData = async (calculatorName: CALCULATORS_NAMES) => {
        const { getResults, evaluateCustomJs } = this;
        const calculatorType = CALCULATORS.find((calc) => calc.name === calculatorName);
        if (!calculatorType) return;
        try {
            const response = await fetch(calculatorType.api);
            const data: CalculatorConfigType = await response.json();
            const tableActiveSection = data.ekaInterpretation ? 'interpretation' : 'how-to-use';
            const newState: State = {
                calculatorConfig: data,
                tableActiveSection,
                elements: data.elements,
                customJs: data.customJs,
            };
            const callback = () => {
                getResults();
                evaluateCustomJs();
            };
            this.setState(newState, callback);
        } catch (error) {
            console.log(error);
        }
    };

    setInterpretationSectionActive = () => {
        this.setState({ tableActiveSection: 'interpretation' });
    };

    setHowToUseSectionActive = () => {
        this.setState({ tableActiveSection: 'how-to-use' });
    };

    onChange = (ref: string, value?: number) => {
        const { elements } = this.state;
        const { getResults, evaluateCustomJs } = this;
        const updatedElements = elements.map((element) => {
            if (element.ref === ref) {
                return {
                    ...element,
                    settings: {
                        ...element.settings,
                        defaultValue: value,
                    },
                };
            }
            return element;
        });
        const newState = {
            elements: updatedElements,
        };
        const callback = () => {
            getResults();
            evaluateCustomJs();
        };
        this.setState(newState, callback);
    };

    getResults = () => {
        const { elements } = this.state;
        const formulas = elements
            .filter((i) => i.elementType === 'formula')
            .filter((i) => !i.ekaHidden);
        const results: { label: string; value: string | number; ignoreInResults?: boolean }[] = [];
        formulas.forEach((i) => {
            if (i && i.settings.expression) {
                const refs = i.settings.expression.match(/#[0-9]+/g) || [];
                let evaluatedExpression = i.settings.expression;
                const allValuesPresent = refs.every((ref) => {
                    const element = elements.find((el) => el.ref === ref);
                    return (
                        element?.settings?.defaultValue !== undefined &&
                        element?.settings?.defaultValue !== null
                    );
                });
                if (allValuesPresent) {
                    refs.forEach((ref) => {
                        const element = elements.find((el) => el.ref === ref);
                        const value = String(element?.settings?.defaultValue || 0);
                        evaluatedExpression = evaluatedExpression.replace(ref, value);
                    });
                    try {
                        let value: string | number = Math.round(eval(evaluatedExpression));
                        i.settings.defaultValue = value;
                        if (i.settings.type === 'date') {
                            value = convertToWeeksAndDays(value);
                        }
                        results.push({
                            label: i.label,
                            value: value,
                            ignoreInResults: i.settings.ignoreInResults,
                        });
                    } catch (error) {
                        results.push({
                            label: i.label,
                            value: 0,
                            ignoreInResults: i.settings.ignoreInResults,
                        });
                        i.settings.defaultValue = 0;
                    }
                }
            }
        });
        this.setState({ results: results });
    };

    evaluateCustomJs = () => {
        const { customJs } = this.state;
        const ctx = {
            getNumberValue: (ref: string) => {
                const { elements } = this.state;
                const element = elements.find((el) => el.ref === ref);
                return element?.settings.defaultValue;
            },
            hideVariables: (...refs: string[]) => {
                const { elements } = this.state;
                const { getResults } = this;
                const updatedElements = elements.map((e) =>
                    refs.includes(e.ref) ? { ...e, ekaHidden: true } : e,
                );
                const newState = { elements: updatedElements };
                this.setState(newState, getResults);
            },
            showVariables: (...refs: string[]) => {
                const { elements } = this.state;
                const { getResults } = this;
                const updatedElements = elements.map((e) =>
                    refs.includes(e.ref) ? { ...e, ekaHidden: false } : e,
                );
                const newState = { elements: updatedElements };
                this.setState(newState, getResults);
            },
        };
        customJs && eval(customJs);
    };

    onSaveInDb = (index: number) => {
        const { closeMe, onElementUpdate, calculatorName } = this.props;
        const { results } = this.state;
        const calculatorType = CALCULATORS.find((calc) => calc.name === calculatorName);
        if (!calculatorType || !results) return;
        const { result } = calculatorType;
        const i = results[index];
        if (!i) {
            notification.error({
                message: `Results not Saved. Please fill in all Sections Correctly.`,
                placement: 'topRight',
                duration: 5,
            });
            return;
        }
        if (i.ignoreInResults) {
            notification.error({
                message: `Result cannot be copied.`,
                placement: 'topRight',
                duration: 5,
            });
            return;
        }
        const calcObj: CalculatorEntity = {
            id: result.id,
            name: result.name,
            dis_name: result.name,
            value: {
                qt: i.value.toString(),
                unit: '',
            },
        };
        onElementUpdate(calcObj);
        closeMe();
    };

    setValue = (ref: string, option?: OptionTypeBaseObj) => {
        const { elements } = this.state;
        const { getResults, evaluateCustomJs } = this;
        const updatedElements = elements.map((element) => {
            if (element.ref === ref) {
                return {
                    ...element,
                    settings: {
                        ...element.settings,
                        defaultValue: Number(option?.id || 0),
                    },
                };
            }
            return element;
        });
        const newState = { elements: updatedElements };
        const callback = () => {
            getResults();
            evaluateCustomJs();
        };
        this.setState(newState, callback);
    };

    setPropertyInputRef = (
        ref: EkaFocosableElement | Input | null,
        index: number,
        subIndex: number,
    ) => {
        if (!this.REF_MAP[index]) {
            this.REF_MAP[index] = [];
        }

        this.REF_MAP[index][subIndex] = ref;
    };

    handleNextFocus = (index: number, subIndex: number) => {
        const currentActiveRow = this.REF_MAP?.[index]?.[subIndex];
        try {
            currentActiveRow?.focus();
        } catch (error) {
            console.error(error);
        }
    };

    render(): ReactNode {
        const { closeMe, calculatorName } = this.props;
        const { calculatorConfig, tableActiveSection, elements, results } = this.state;
        const {
            setInterpretationSectionActive,
            setHowToUseSectionActive,
            onChange,
            onSaveInDb,
        } = this;

        const calculatorType = CALCULATORS.find((calc) => calc.name === calculatorName);

        return (
            <Hotkeys keyName="esc" onKeyDown={closeMe}>
                <RightPopUp closeMe={closeMe}>
                    <div className="w-full h-full bg-darwin-neutral-50 flex flex-col px-16 py-18 text-14 font-400">
                        <div className="flex items-center space-x-16">
                            <svg
                                onClick={closeMe}
                                className="w-13 h-14 fill-darwin-neutral-1000 cursor-pointer"
                                viewBox="0 0 13 12"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path d="M12.5977 5.71875C12.5977 6.10156 12.2969 6.40234 11.9414 6.40234H2.64453L6.28125 9.84766C6.55469 10.0938 6.55469 10.5312 6.30859 10.7773C6.0625 11.0508 5.65234 11.0508 5.37891 10.8047L0.566406 6.21094C0.429688 6.07422 0.375 5.91016 0.375 5.71875C0.375 5.55469 0.429688 5.39062 0.566406 5.25391L5.37891 0.660156C5.65234 0.414062 6.0625 0.414062 6.30859 0.6875C6.55469 0.933594 6.55469 1.37109 6.28125 1.61719L2.64453 5.0625H11.9414C12.3242 5.0625 12.5977 5.36328 12.5977 5.71875Z" />
                            </svg>
                            <p className="text-16 font-700">
                                {calculatorType?.title?.toUpperCase()}
                            </p>
                        </div>

                        {calculatorConfig?.ekaDescription ? (
                            <div className="flex items-center space-x-10 p-12 bg-white mt-12 mb-16 rounded-4">
                                <svg
                                    className="min-w-14 max-w-14 h-14 fill-darwin-neutral-900"
                                    viewBox="0 0 15 15"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path d="M7.5 0.75C3.61719 0.75 0.5 3.89453 0.5 7.75C0.5 11.6328 3.61719 14.75 7.5 14.75C11.3555 14.75 14.5 11.6328 14.5 7.75C14.5 3.89453 11.3555 0.75 7.5 0.75ZM7.5 4.25C7.96484 4.25 8.375 4.66016 8.375 5.125C8.375 5.61719 7.96484 6 7.5 6C7.00781 6 6.625 5.61719 6.625 5.125C6.625 4.66016 7.00781 4.25 7.5 4.25ZM8.59375 11.25H6.40625C6.02344 11.25 5.75 10.9766 5.75 10.5938C5.75 10.2383 6.02344 9.9375 6.40625 9.9375H6.84375V8.1875H6.625C6.24219 8.1875 5.96875 7.91406 5.96875 7.53125C5.96875 7.17578 6.24219 6.875 6.625 6.875H7.5C7.85547 6.875 8.15625 7.17578 8.15625 7.53125V9.9375H8.59375C8.94922 9.9375 9.25 10.2383 9.25 10.5938C9.25 10.9766 8.94922 11.25 8.59375 11.25Z" />
                                </svg>
                                <p>{calculatorConfig?.ekaDescription}</p>
                            </div>
                        ) : (
                            <div className="mb-24"></div>
                        )}

                        <div className="flex-1 flex gap-x-16 overflow-hidden">
                            <div className="w-60p bg-white flex flex-col rounded-4">
                                <p className="text-16 font-700 px-16 py-10 border-b border-darwin-neutral-100">
                                    {calculatorConfig?.title}
                                </p>

                                <div className="flex-1 overflow-auto p-16 space-y-16">
                                    {elements
                                        ?.filter((i) => validElementTypes.has(i.elementType))
                                        ?.filter((i) => !i.ekaHidden)
                                        .map((i, index) => {
                                            switch (i.elementType) {
                                                case SLIDER:
                                                    return (
                                                        <SliderInput
                                                            element={i}
                                                            onChange={onChange}
                                                        />
                                                    );
                                                case SELECT:
                                                    return (
                                                        <SelectInput
                                                            index={index}
                                                            element={i}
                                                            setValue={this.setValue}
                                                            handleNextFocus={this.handleNextFocus}
                                                            setPropertyInputRef={
                                                                this.setPropertyInputRef
                                                            }
                                                        />
                                                    );
                                                case DATETIME:
                                                    return (
                                                        <DateTimeInput
                                                            index={index}
                                                            element={i}
                                                            setValue={this.onChange}
                                                            handleNextFocus={this.handleNextFocus}
                                                            setPropertyInputRef={
                                                                this.setPropertyInputRef
                                                            }
                                                        />
                                                    );
                                                default:
                                                    return null;
                                            }
                                        })}
                                </div>

                                <div className="w-full flex pb-24 px-16 justify-end">
                                    {results && results.length > 1 ? (
                                        <Popover
                                            content={
                                                <Menu className="max-h-328 overflow-auto">
                                                    {results?.map((i, index) => (
                                                        <Menu.Item
                                                            key={i.label}
                                                            onClick={() => onSaveInDb(index)}
                                                            disabled={i.ignoreInResults}
                                                        >
                                                            {index + 1}. {i.label} ({i.value})
                                                        </Menu.Item>
                                                    ))}
                                                </Menu>
                                            }
                                            overlayClassName="popover-p-0"
                                        >
                                            <button className="primary-btn px-50 py-8 btn btn--small space-x-8">
                                                <span className="w-full text-center">
                                                    Copy to Pad
                                                </span>
                                            </button>
                                        </Popover>
                                    ) : (
                                        <button
                                            className="primary-btn px-50 py-8 btn btn--small space-x-8"
                                            onClick={() => onSaveInDb(0)}
                                            disabled={results?.[0]?.ignoreInResults}
                                        >
                                            <span className="w-full text-center">Copy to Pad</span>
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="w-40p min-w-40p flex flex-col space-y-12">
                                <div className="bg-white rounded-4 min-h-136">
                                    <p className="text-16 font-700 px-16 py-10 border-b border-darwin-neutral-100">
                                        Result
                                    </p>
                                    <div className="space-y-10 py-12">
                                        {results?.map((i, index) => (
                                            <div className="px-16 space-y-5">
                                                <p>{i.label}</p>
                                                <button
                                                    className="text-darwin-accent-bright-blue-800 cursor-pointer disabled:cursor-not-allowed text-left"
                                                    onClick={() => onSaveInDb(index)}
                                                    disabled={i.ignoreInResults}
                                                >
                                                    <AntTooltip
                                                        content={'Copy to Pad'}
                                                        placement={'right'}
                                                    >
                                                        <span
                                                            className={`${
                                                                typeof i.value === 'string'
                                                                    ? 'text-40'
                                                                    : 'text-57'
                                                            } font-700`}
                                                        >
                                                            {i.value}
                                                        </span>
                                                    </AntTooltip>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {(calculatorConfig?.ekaInterpretation ||
                                    calculatorConfig?.ekaHowToUse) && (
                                    <div className="flex flex-col bg-white px-16 pb-10 rounded-4 overflow-hidden flex-1">
                                        <div className="sticky top-0 flex bg-white">
                                            {calculatorConfig?.ekaInterpretation && (
                                                <button
                                                    className={`flex-1 ${
                                                        tableActiveSection === 'interpretation'
                                                            ? 'calc-active-btn'
                                                            : 'border-b border-darwin-neutral-100'
                                                    } flex items-center justify-center space-x-8 px-16 py-10`}
                                                    onClick={setInterpretationSectionActive}
                                                >
                                                    <svg
                                                        className="min-w-12 max-w-12 min-h-12 max-h-12 relative top-1 fill-darwin-neutral-900"
                                                        viewBox="0 0 14 11"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path d="M12.8594 9.27344C13.2344 9.92969 12.7656 10.75 12.0156 10.75H1.98438C1.21094 10.75 0.742188 9.92969 1.14062 9.27344L6.13281 0.742188C6.53125 0.0859375 7.49219 0.0859375 7.84375 0.742188L12.8594 9.27344Z" />
                                                    </svg>
                                                    <span>Interpretation</span>
                                                </button>
                                            )}
                                            {calculatorConfig?.ekaHowToUse && (
                                                <button
                                                    className={`flex-1 ${
                                                        tableActiveSection === 'how-to-use'
                                                            ? 'calc-active-btn'
                                                            : 'border-b border-darwin-neutral-100'
                                                    } flex items-center justify-center space-x-8 px-16 py-10`}
                                                    onClick={setHowToUseSectionActive}
                                                >
                                                    <svg
                                                        className="w-8 h-12 relative top-1 fill-darwin-neutral-900"
                                                        viewBox="0 0 8 11"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path d="M5.03125 0.273438H2.5C1.25781 0.273438 0.25 1.28125 0.25 2.52344C0.25 2.92188 0.578125 3.25 1 3.25C1.39844 3.25 1.75 2.89844 1.75 2.52344C1.75 2.10156 2.07812 1.77344 2.5 1.77344H5.03125C5.6875 1.77344 6.25 2.3125 6.25 2.96875C6.25 3.4375 5.99219 3.85938 5.52344 4.09375L3.22656 5.42969C2.99219 5.57031 2.875 5.80469 2.875 6.0625V7C2.875 7.42188 3.20312 7.75 3.625 7.75C4.02344 7.75 4.375 7.42188 4.375 7V6.50781L6.25 5.40625C7.16406 4.9375 7.75 4 7.75 2.96875C7.75 1.46875 6.53125 0.273438 5.03125 0.273438ZM3.625 8.875C3.08594 8.875 2.6875 9.29688 2.6875 9.8125C2.6875 10.3516 3.08594 10.75 3.625 10.75C4.14062 10.75 4.5625 10.3281 4.5625 9.8125C4.5625 9.32031 4.14062 8.875 3.625 8.875Z" />
                                                    </svg>
                                                    <span>How to use</span>
                                                </button>
                                            )}
                                        </div>

                                        {tableActiveSection === 'how-to-use' && calculatorConfig && (
                                            <div
                                                className="py-16 flex-1 overflow-auto"
                                                dangerouslySetInnerHTML={{
                                                    __html: calculatorConfig?.ekaHowToUse,
                                                }}
                                            ></div>
                                        )}

                                        {tableActiveSection === 'interpretation' &&
                                        calculatorConfig ? (
                                            calculatorConfig?.ekaInterpretation?.type === 'html' ? (
                                                <div
                                                    className="py-16 flex-1 overflow-auto"
                                                    dangerouslySetInnerHTML={{
                                                        __html:
                                                            calculatorConfig?.ekaInterpretation
                                                                .info,
                                                    }}
                                                ></div>
                                            ) : (
                                                <div className="overflow-hidden flex-1 mt-16">
                                                    <CalculatorInterPretationTable
                                                        ekaInterpretation={
                                                            calculatorConfig.ekaInterpretation
                                                        }
                                                    />
                                                </div>
                                            )
                                        ) : null}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </RightPopUp>
            </Hotkeys>
        );
    }
}

export default NewCalculator;
